import React from 'react'
import { RefreshCcw, ChevronDown, ChevronUp } from 'lucide-react'
import { Button } from '../../../../components/ui/button'
import { Badge } from '../../../../components/ui/badge'
import PatronRiskDoughnut from '../PatronRiskDoughnut'
import CddTierCard from '../CddTierCard'
import { filterFactorsByDomain, primaryRiskDriver } from '../../../../lib/patronRiskBreakdown'
import { formatRiskScoreNumber } from '../../../../components/patron/riskScoreMeta'
import ProfilePanel from './ProfilePanel'

import { formatSydneyDate } from '../../../../utils/sydneyDateTime'
export default function ProfileRiskSection({
  profileData,
  profileId,
  effectiveRisk,
  riskBreakdown,
  riskBreakdownLoading,
  riskRecalculating,
  selectedRiskDomain,
  onSelectRiskDomain,
  isContributingFactorsExpanded,
  onToggleContributingFactors,
  cddRefreshKey,
  onTierLoaded,
  onRecalculateRisk,
  parseRiskFactors,
}) {
  const riskFactors = parseRiskFactors(profileData?.RiskFactors)
  const driver = primaryRiskDriver(riskFactors)
  const visibleFactors = filterFactorsByDomain(riskFactors, selectedRiskDomain)

  if (!profileData?.RiskLevel && effectiveRisk?.score == null) {
    return (
      <ProfilePanel title="Risk analysis">
        <p className="text-sm text-slate-500 m-0">No risk assessment on file yet.</p>
        <Button
          type="button"
          size="sm"
          className="mt-3"
          disabled={riskRecalculating}
          onClick={onRecalculateRisk}
        >
          <RefreshCcw className={`h-3.5 w-3.5 mr-1 ${riskRecalculating ? 'animate-spin' : ''}`} />
          {riskRecalculating ? 'Calculating…' : 'Calculate risk'}
        </Button>
      </ProfilePanel>
    )
  }

  return (
    <ProfilePanel
      title="Risk analysis"
      hint={
        profileData?.RiskAssessedAt
          ? `Assessed ${formatSydneyDate(new Date(profileData.RiskAssessedAt))}`
          : null
      }
      actions={
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="text-xs h-8"
          disabled={riskRecalculating || !profileData?.MID}
          onClick={onRecalculateRisk}
        >
          <RefreshCcw className={`h-3.5 w-3.5 mr-1 ${riskRecalculating ? 'animate-spin' : ''}`} />
          {riskRecalculating ? 'Recalculating…' : 'Recalculate'}
        </Button>
      }
    >
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <Badge
          className={`border ${
            effectiveRisk.level === 'CRITICAL'
              ? 'bg-red-100 text-red-800 border-red-300'
              : effectiveRisk.level === 'HIGH'
                ? 'bg-orange-100 text-orange-800 border-orange-300'
                : effectiveRisk.level === 'MEDIUM'
                  ? 'bg-yellow-100 text-yellow-800 border-yellow-300'
                  : 'bg-green-100 text-green-800 border-green-300'
          }`}
        >
          {effectiveRisk.level || profileData.RiskLevel}
        </Badge>
        <span className="text-sm font-semibold text-slate-700">
          {formatRiskScoreNumber(effectiveRisk.score)}/100
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 items-stretch mb-6">
        <div className="lg:col-span-3 p-4 rounded-lg border border-slate-200 bg-white min-w-0">
          <PatronRiskDoughnut
            riskScore={effectiveRisk.score}
            riskLevel={effectiveRisk.level || profileData.RiskLevel}
            components={riskBreakdown?.components}
            weights={riskBreakdown?.weights}
            riskFactors={riskFactors}
            loading={riskBreakdownLoading && !riskBreakdown?.components && riskFactors.length === 0}
            selectedDomainId={selectedRiskDomain}
            onSelectDomain={onSelectRiskDomain}
          />
          {driver && (
            <p className="text-sm text-slate-600 mt-4 pt-4 border-t border-slate-100">
              <span className="font-medium text-slate-800">Primary driver: </span>
              {driver.name}
              {driver.detail ? ` — ${driver.detail}` : ''}
            </p>
          )}
        </div>
        <div className="lg:col-span-2 min-w-0">
          <CddTierCard
            memberId={profileData.MID || profileId}
            refreshKey={cddRefreshKey}
            onTierLoaded={onTierLoaded}
            sidebar
          />
        </div>
      </div>

      {riskFactors.length > 0 && (
        <div>
          <div className="flex flex-wrap justify-between items-center gap-2 mb-3">
            <button
              type="button"
              className="flex items-center gap-2 text-left group bg-transparent border-0 cursor-pointer p-0"
              onClick={onToggleContributingFactors}
              aria-expanded={isContributingFactorsExpanded}
            >
              <h4 className="text-sm font-semibold text-slate-700 group-hover:text-slate-900 m-0">
                Contributing factors
              </h4>
              <span className="text-xs text-slate-500">
                ({visibleFactors.filter((f) => f?.factor !== 'Risk model' || (f.score || 0) > 0).length})
              </span>
              {isContributingFactorsExpanded ? (
                <ChevronUp className="h-4 w-4 text-slate-500" />
              ) : (
                <ChevronDown className="h-4 w-4 text-slate-500" />
              )}
            </button>
            {selectedRiskDomain && (
              <button
                type="button"
                className="text-xs text-teal-700 hover:underline bg-transparent border-0 cursor-pointer p-0"
                onClick={() => onSelectRiskDomain(null)}
              >
                Show all domains
              </button>
            )}
          </div>
          {isContributingFactorsExpanded && (
            <div className="space-y-2">
              {visibleFactors
                .filter((f) => f?.factor !== 'Risk model' || (f.score || 0) > 0)
                .map((factor, idx) => (
                  <div
                    key={`${factor.factor}-${idx}`}
                    className="flex items-start p-3 rounded bg-orange-50 border border-orange-200"
                  >
                    <div className="flex-1">
                      <div className="flex justify-between items-start gap-2">
                        <div className="min-w-0">
                          <p className="font-medium text-sm text-slate-900 m-0">
                            {String(factor.factor || '').replace(/_/g, ' ')}
                          </p>
                          {factor.detail && (
                            <p className="text-xs text-slate-600 mt-1 mb-0">{factor.detail}</p>
                          )}
                          {factor.component && (
                            <p className="text-xs text-slate-400 mt-0.5 mb-0">{factor.component}</p>
                          )}
                        </div>
                        {(factor.score || 0) > 0 && (
                          <Badge variant="outline" className="text-xs ml-2 shrink-0">
                            +{factor.score} pts
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              {visibleFactors.length === 0 && selectedRiskDomain && (
                <p className="text-sm text-slate-500 m-0">No factors in this domain.</p>
              )}
            </div>
          )}
        </div>
      )}
    </ProfilePanel>
  )
}
